import React from 'react';
import { useParams } from 'react-router-dom';
import { batch } from 'react-redux';
import { useMutation } from '@apollo/client';
import {
  IDashboardTemplateFieldsFragment,
  IDashboardUnpublishMutation,
  IDashboardUnpublishMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { UNPUBLISH_DASHBOARD } from '../../../../utils/apollo/dashboards';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectModalData } from '../../../../redux/selectors/modal';
import { assertNotNil } from '../../../../utils/types';
import { closeModal } from '../../../../redux/slices/modal';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import {
  reloadDashboardTemplates,
  dashboardInstanceUpdatePath,
} from '../../../../redux/slices/dashboards/dashboardInstance';
import toastMessage from '../../../../utils/toastMessage';

const DashboardUnpublishModal = () => {
  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const { dashboardBaseId } = urlParams;

  const handleClose = () => {
    dispatch(closeModal());
  };

  const dashboard: IDashboardTemplateFieldsFragment =
    useAppSelector(selectModalData);
  assertNotNil(dashboard);

  const handleComplete = ({
    dashboardUnpublish,
  }: IDashboardUnpublishMutation) => {
    const { entity, errors } = dashboardUnpublish;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);

    batch(() => {
      dispatch(reloadDashboardTemplates());

      if (entity.baseId === dashboardBaseId) {
        // Update instance path
        dispatch(dashboardInstanceUpdatePath({ newPath: entity.path }));
      }
    });

    handleClose();
    toastMessage({
      message: `Dashboard ${entity.name} has been unpublished`,
      type: 'info',
    });
  };

  const [unpublishDashboard, { loading }] = useMutation<
    IDashboardUnpublishMutation,
    IDashboardUnpublishMutationVariables
  >(UNPUBLISH_DASHBOARD, {
    variables: {
      input: {
        baseId: dashboard.baseId,
      },
    },
    onCompleted: handleComplete,
  });

  return (
    <ConfirmationModal
      open
      onClose={handleClose}
      onSubmit={unpublishDashboard}
      header={'Unpublish Confirmation'}
      confirmActionLabel={'Unpublish'}
      disabled={loading}
      trackContext="dashboards"
      trackConfirmAction="unpublish - confirm"
      trackCancelAction="unpublish - cancel"
    >
      Do you want to unpublish Dashboard <strong>{dashboard.name}</strong>? This
      action cannot be undone.
    </ConfirmationModal>
  );
};

export default DashboardUnpublishModal;
